import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import {
  fetchAssistants,
  createAssistant,
  updateAssistantPermissions,
  clearAssistantStatus,
} from "../../Store/Actions/AssistantActions";

const PERMISSION_FIELDS = [
  { key: "events", label: "Create or Update Events" },
  { key: "resources", label: "Create or Update Resource" },
  { key: "collaboration", label: "Collaboration Workspace" },
  { key: "exploreNonprofits", label: "Explore Other Non Profit" },
  { key: "inquiries", label: "Create TCC Plus Support Inquiries" },
  { key: "personalSpace", label: "Personal Space (Upload or Read)" },
  { key: "notifications", label: "Notifications" },
];

const DEFAULT_PERMISSIONS = {
  events: false,
  resources: false,
  collaboration: false,
  exploreNonprofits: false,
  inquiries: false,
  personalSpace: false,
  notifications: false,
};

const AssistantFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list, createLoading, updateLoading } = useSelector((s) => s.assistants);
  const existing = isEdit ? list.find((a) => a.id === id) : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newConfirmPassword, setNewConfirmPassword] = useState("");
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && list.length === 0) dispatch(fetchAssistants());
  }, [dispatch, isEdit, list.length]);

  useEffect(() => {
    if (existing) {
      setEmail(existing.email);
      setPermissions(existing.permissions);
    }
  }, [existing]);

  useEffect(() => () => dispatch(clearAssistantStatus()), [dispatch]);

  const togglePermission = (key) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validate = () => {
    const errs = {};
    if (!isEdit) {
      if (!email.trim()) errs.email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Invalid email";
      if (!password) errs.password = "Password is required";
      else if (password.length < 8) errs.password = "Minimum 8 characters";
      if (!confirmPassword) errs.confirmPassword = "Please confirm password";
      else if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match";
    } else {
      if (newPassword || newConfirmPassword) {
        if (newPassword.length < 8) errs.newPassword = "Minimum 8 characters";
        if (!newConfirmPassword) errs.newConfirmPassword = "Please confirm new password";
        else if (newPassword !== newConfirmPassword) errs.newConfirmPassword = "Passwords do not match";
      }
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    try {
      if (isEdit) {
        await dispatch(updateAssistantPermissions(id, permissions, showPasswordChange ? newPassword : undefined, showPasswordChange ? newConfirmPassword : undefined));
        toast.success("Assistant updated");
      } else {
        await dispatch(createAssistant({ email, password, confirmPassword, permissions }));
        toast.success("Assistant created");
      }
      navigate("/dashboard/assistants");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    }
  };

  const isLoading = isEdit ? updateLoading : createLoading;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/dashboard/assistants")}
        className="flex items-center gap-1 text-sm text-textColor hover:text-themeColor mb-5 font-popinsMedium"
      >
        <ChevronLeft className="size-4" /> Back
      </button>

      <h1 className="font-popinsBold text-2xl mb-6">
        {isEdit ? "Edit Assistant" : "Add Assistant"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-popinsSemiBold text-base text-darkTheme">Account Info</h2>

          {/* Email — always shown; disabled on edit */}
          <div>
            <label className="block text-sm font-popinsMedium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => !isEdit && setEmail(e.target.value)}
              disabled={isEdit}
              placeholder="assistant@example.com"
              className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${
                isEdit
                  ? "bg-gray-50 text-textColor cursor-not-allowed border-gray-200"
                  : `focus:ring-2 focus:ring-themeColor ${errors.email ? "border-red-400" : "border-gray-200"}`
              }`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Add mode: set initial password */}
          {!isEdit && (
            <>
              <div>
                <label className="block text-sm font-popinsMedium mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-themeColor ${
                    errors.password ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-popinsMedium mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-themeColor ${
                    errors.confirmPassword ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>
            </>
          )}

          {/* Edit mode: password change toggle */}
          {isEdit && (
            <>
              <div>
                <label className="block text-sm font-popinsMedium mb-1">Password</label>
                <div className="flex items-center gap-3">
                  <input
                    type="password"
                    value="••••••••"
                    disabled
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-textColor cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordChange((v) => !v);
                      setNewPassword("");
                      setNewConfirmPassword("");
                    }}
                    className="text-xs font-popinsSemiBold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition whitespace-nowrap"
                  >
                    {showPasswordChange ? "Cancel" : "Change Password"}
                  </button>
                </div>
              </div>

              {showPasswordChange && (
                <>
                  <div>
                    <label className="block text-sm font-popinsMedium mb-1">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-themeColor ${
                        errors.newPassword ? "border-red-400" : "border-gray-200"
                      }`}
                    />
                    {errors.newPassword && <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-popinsMedium mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={newConfirmPassword}
                      onChange={(e) => setNewConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-themeColor ${
                        errors.newConfirmPassword ? "border-red-400" : "border-gray-200"
                      }`}
                    />
                    {errors.newConfirmPassword && <p className="text-red-500 text-xs mt-1">{errors.newConfirmPassword}</p>}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-popinsSemiBold text-base text-darkTheme">Permissions</h2>
          <div className="space-y-3">
            {PERMISSION_FIELDS.map(({ key, label }) => (
              <label key={key} className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-popinsMedium">{label}</span>
                <button
                  type="button"
                  onClick={() => togglePermission(key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    permissions[key] ? "bg-themeColor" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                      permissions[key] ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-themeColor text-white py-2.5 rounded-lg font-popinsSemiBold text-sm hover:opacity-90 transition disabled:opacity-60"
        >
          {isLoading ? "Saving…" : isEdit ? "Save Changes" : "Create Assistant"}
        </button>
      </form>
    </div>
  );
};

export default AssistantFormPage;
